import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inviteApi } from '../services/api';
import api from '../services/api';

const PERMS = [
  { key: 'can_manage_players', label: 'Manage Players' },
  { key: 'can_generate_draw',  label: 'Generate Draw'  },
  { key: 'can_schedule',       label: 'Schedule Matches'},
  { key: 'can_score',          label: 'Score Matches'  },
  { key: 'can_edit_settings',  label: 'Edit Settings'  },
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if the invite email already has an account (shown after invite loads)
  const [isExistingUser, setIsExistingUser] = useState(null); // null = unknown

  // Form state
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    inviteApi.preview(token)
      .then(res => {
        setInvite(res.data);
        // Try to infer if existing user — the backend will tell us on accept
        // We detect via a simple heuristic: if the current logged-in user's email matches, skip form
      })
      .catch(err => {
        setLoadError(err.response?.data?.error || 'Invitation not found or expired');
      })
      .finally(() => setLoading(false));
  }, [token]);

  // If the currently logged-in user IS the invite target email, accept directly
  const isCurrentUserTheTarget = currentUser && invite && currentUser.email === invite.email;

  const handleAccept = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const body = isCurrentUserTheTarget
        ? {} // already authenticated — no password needed
        : isExistingUser
          ? { password }
          : { name, password };

      const res = await inviteApi.accept(token, body);
      const { token: jwtToken, user: userData } = res.data;

      // Store auth — same pattern as AuthContext.login()
      localStorage.setItem('token', jwtToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

      setAccepted(true);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  // ── Error ──────────────────────────────────────────────────────────────────

  if (loadError) return (
    <div style={{ maxWidth: 480, margin: '4rem auto', padding: '0 1rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔗</div>
      <h2 style={{ marginBottom: '0.5rem' }}>Invitation Unavailable</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{loadError}</p>
      <Link to="/tournaments" className="btn btn-primary">Browse Tournaments</Link>
    </div>
  );

  // ── Success ────────────────────────────────────────────────────────────────

  if (accepted) return (
    <div style={{ maxWidth: 480, margin: '4rem auto', padding: '0 1rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
      <h2 style={{ marginBottom: '0.5rem' }}>You're on the team!</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        You now have staff access to <strong>{invite.tournament.name}</strong>.
      </p>
      <button
        className="btn btn-primary"
        onClick={() => navigate(`/tournaments/${invite.tournament.id}`)}
      >
        Go to Tournament →
      </button>
    </div>
  );

  // ── Invitation preview + accept form ──────────────────────────────────────

  const grantedPerms = PERMS.filter(p => invite.permissions[p.key]);

  return (
    <div style={{ maxWidth: 520, margin: '3rem auto', padding: '0 1rem' }}>
      {/* Invite preview card */}
      <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center', padding: '1.5rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎾</div>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem' }}>You've been invited!</h2>
        <p style={{ color: '#6b7280', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
          <strong>{invite.invited_by}</strong> wants you to help manage
        </p>
        <p style={{ margin: '0 0 1rem', fontWeight: '800', fontSize: '1.1rem', color: 'var(--primary-color)' }}>
          {invite.tournament.name}
        </p>

        {grantedPerms.length > 0 && (
          <div style={{ margin: '0 auto 1rem', maxWidth: 320 }}>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 0.4rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Permissions granted</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', justifyContent: 'center' }}>
              {grantedPerms.map(p => (
                <span key={p.key} style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 999, border: '1px solid #6ee7b7' }}>
                  {p.label}
                </span>
              ))}
            </div>
          </div>
        )}

        <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: 0 }}>
          Expires {fmtDate(invite.expires_at)}
        </p>
      </div>

      {/* If current logged-in user is the target — one-click accept */}
      {isCurrentUserTheTarget ? (
        <div className="card">
          <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#374151' }}>
            You're logged in as <strong>{currentUser.email}</strong>. Click below to accept.
          </p>
          {formError && (
            <div style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' }}>
              {formError}
            </div>
          )}
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleAccept} disabled={submitting}>
            {submitting ? 'Accepting…' : 'Accept Invitation'}
          </button>
        </div>
      ) : isExistingUser === null ? (
        /* Ask if they have an account */
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '1rem', color: '#374151', fontSize: '0.9rem' }}>
            This invitation is for <strong>{invite.email}</strong>. Do you already have an account?
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={() => setIsExistingUser(true)}>Yes, I have an account</button>
            <button className="btn btn-outline" onClick={() => setIsExistingUser(false)}>No, create one</button>
          </div>
        </div>
      ) : isExistingUser ? (
        /* Login form */
        <div className="card">
          <h3 style={{ margin: '0 0 0.25rem' }}>Sign in to accept</h3>
          <p style={{ color: '#6b7280', fontSize: '0.82rem', margin: '0 0 1rem' }}>
            Enter your password for <strong>{invite.email}</strong>
          </p>
          <form onSubmit={handleAccept}>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                required
              />
            </div>
            {formError && (
              <div style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                {formError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setIsExistingUser(null); setFormError(''); setPassword(''); }}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign In & Accept'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Register form */
        <div className="card">
          <h3 style={{ margin: '0 0 0.25rem' }}>Create your account</h3>
          <p style={{ color: '#6b7280', fontSize: '0.82rem', margin: '0 0 1rem' }}>
            Your account will be created with email <strong>{invite.email}</strong>
          </p>
          <form onSubmit={handleAccept}>
            <div className="form-group">
              <label className="form-label">Your Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Full name"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
            </div>
            {formError && (
              <div style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                {formError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setIsExistingUser(null); setFormError(''); setPassword(''); setName(''); }}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Account & Accept'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default InviteAccept;
